import { IEditorMetadata } from "@mctypes/global.types";
import { IMeeting } from "@mctypes/meeting.types";
import { openapi } from "../../../../openapi";
import { getCacheKey } from "../../../apiSlice";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { trpcSlice } from "../../../trpcSlice";
import { meetingRTK } from "./meetingRTK";

export const meetingAgendaRTK = trpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    createMeetingAgendaItem: builder.mutation<IMeeting.AgendaItem, { projectId: string; meetingId: string }>({
      query: (input) => ({
        url: `/project/${input.projectId}/meetingagenda/meeting/${input.meetingId}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["MeetingAgenda", "Meeting"],
    }),
    updateMeetingAgenda: builder.mutation<
      IMeeting.AgendaItem,
      { projectId: string; agendaId: string; attribute: string; value: any }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/${input.agendaId}/attribute`,
          method: "PUT",
          body: {
            attribute: input.attribute,
            value: input.value,
          },
        };
      },
      async onQueryStarted({ agendaId, value, attribute }, { dispatch, queryFulfilled, getState }) {
        patchAgendaItemAfterUpdateWithOptimisticUpdate(agendaId, value, attribute, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    addAgendaPreperationItemTodo: builder.mutation<
      IMeeting.AgendaItem,
      { projectId: string; agendaId: string; text: string }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/todo/${input.agendaId}/add`,
          method: "POST",
          body: { text: input.text },
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    updateAgendaPreperationItemToDo: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        todoId: string;
        text: string;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/todo/${input.agendaId}/update`,
          method: "PUT",
          body: {
            text: input.text,
            todoId: input.todoId,
          },
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    toggleAgendaPreperationItemTodo: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        todoId: string;
        isChecked: boolean;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/todo/${input.agendaId}/toggle`,
          method: "PUT",
          body: {
            isChecked: input.isChecked,
            todoId: input.todoId,
          },
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    deleteAgendaPreperationItemToDo: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        todoId: string;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/todo/${input.agendaId}/delete/${input.todoId}`,
          method: "DELETE",
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    connectOrDisconnectIssueFromAgendaItem: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        issueId: string;
        connectOrDisconnect: "CONNECT" | "DISCONNECT";
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/${input.agendaId}/connect-disconnect-issue/${input.issueId}`,
          method: "PUT",
          body: {
            connectOrDisconnect: input.connectOrDisconnect,
          },
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    addAgendaComment: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        commentId: string;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/${input.agendaId}/comment/${input.commentId}`,
          method: "POST",
          body: {},
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    deleteAgendaComment: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        commentId: string;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/${input.agendaId}/comment/${input.commentId}`,
          method: "DELETE",
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    updateAgendaComment: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        commentId: string;
        text: string;
        agendaId: string;
        metadata: IEditorMetadata;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/comment/${input.commentId}`,
          method: "PUT",
          body: {
            text: input.text,
            metadata: input.metadata,
          },
        };
      },
      invalidatesTags: ["Meeting", "MeetingAgenda"],
    }),
    reactToAgendaComment: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        commentId: string;
        emoji: string;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/${input.agendaId}/comment/${input.commentId}/reaction`,
          method: "PUT",
          body: {
            emoji: input.emoji,
          },
        };
      },
      async onQueryStarted({ agendaId }, { dispatch, queryFulfilled, getState }) {
        replaceAgendaItemWithPessimisticUpdate(agendaId, {
          dispatch,
          queryFulfilled,
          getState,
        });
      },
    }),
    updateAgendaOrder: builder.mutation<
      IMeeting.AgendaItem,
      {
        projectId: string;
        agendaId: string;
        order: number;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/meetingagenda/${input.agendaId}/order`,
          method: "PUT",
          body: {
            order: input.order,
          },
        };
      },
      invalidatesTags: ["Meeting"],
    }),
  }),
});

export const {
  useUpdateMeetingAgendaMutation,
  useAddAgendaPreperationItemTodoMutation,
  useDeleteAgendaPreperationItemToDoMutation,
  useUpdateAgendaPreperationItemToDoMutation,
  useToggleAgendaPreperationItemTodoMutation,
  useAddAgendaCommentMutation,
  useUpdateAgendaCommentMutation,
  useReactToAgendaCommentMutation,
  useDeleteAgendaCommentMutation,
  useConnectOrDisconnectIssueFromAgendaItemMutation,
  useCreateMeetingAgendaItemMutation,
  useUpdateAgendaOrderMutation,
} = meetingAgendaRTK;

const patchAgendaItemAfterUpdateWithOptimisticUpdate = async (
  agendaId,
  value,
  attribute,
  { dispatch, queryFulfilled, getState },
) => {
  if (attribute === "notRelevant" || attribute === "duration") {
    //invalidate tag since we need to update the list of agenda items
    dispatch(meetingRTK.util.invalidateTags(["Meeting"]));
    return;
  }

  const queries = getState().trpc.queries;
  const generatedCacheKey = getCacheKey(queries, "getProjectByProjectIdMeetingAndMeetingId");
  const patchAgendaItem = (draft) => {
    draft.agendaItems.map((agendaItem) => {
      if (agendaItem._id === agendaId) {
        agendaItem[attribute] = value;
      }
      return agendaItem;
    });
  };
  const patchResults = [
    generatedCacheKey != null
      ? dispatch(openapi.util.updateQueryData("getProjectByProjectIdMeetingAndMeetingId", generatedCacheKey, patchAgendaItem))
      : null,
  ].filter(Boolean);
  try {
    await queryFulfilled;
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  } catch {
    alert("Kunne ikke oppdatere. Prøv igjen. Hvis problemene vedvarer, kontakt support på mcapps@multiconsult.no");
    // dispatch(meetingAgendaRTK.util.invalidateTags(["MeetingAgenda", "Meeting"]));
    patchResults.forEach((patchResult) => patchResult.undo());
  }
};


const replaceAgendaItemWithPessimisticUpdate = async (agendaId, { dispatch, queryFulfilled, getState }) => {
  const resp = await queryFulfilled;
  const data: IMeeting.AgendaItem = resp.data;
  const queries = getState().trpc.queries;
  const generatedCacheKey = getCacheKey(queries, "getProjectByProjectIdMeetingAndMeetingId");
  const replaceAgendaItem = (draft) => {
    draft.agendaItems.map((agendaItem) => {
      if (agendaItem._id === agendaId) {
        Object.assign(agendaItem, data);
      }
      return agendaItem;
    });
  };
  if (generatedCacheKey != null) {
    dispatch(openapi.util.updateQueryData("getProjectByProjectIdMeetingAndMeetingId", generatedCacheKey, replaceAgendaItem));
  }
  dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
};

